import { ActionIcon, Button, LoadingOverlay, Table } from "@mantine/core"
import { IconArrowBadgeRight, IconTrash } from "@tabler/icons-react"
import { debounce } from "lodash-es"
import { lazy, Suspense, useEffect, useState } from "react"
import { CollectionQuery } from "../../../models/collection.model"
import { Permission } from "../../../models/permission.model"
import { Role } from "../../../models/role.model"
import Card from "../../../shared/component/Card/card-component"
import { CollectionSelectorConfig } from "../../../shared/component/collection-selector/model/collection-selector-config"
import EmptyIcon from "../../../shared/component/empty-icon/empty-icon"
import { Pagination } from "../../../shared/component/pagination/pagination"
import {
  useCreateRolePermissionsMutation,
  useLazyGetAvailableRolePermissionsQuery,
  useLazyGetPermissionsQuery,
  useLazyGetRolesQuery,
  useRemoveRolePermissionsMutation,
} from "../store/role.query"
import { useTranslation } from "react-i18next"
import i18n from "../../../shared/i18n/config"
const ModalCollectionSelector = lazy(
  () =>
    import(
      "../../../shared/component/collection-selector/components/modal-collection-selector"
    ),
)
const collectionSelectorModalConfig: CollectionSelectorConfig = {
  multiple: true,
  size: "40%",
  identity: "id",
  visibleColumn: [{ key: "name", name: i18n.t("name") }],
  primaryColumn: { key: "name", name: i18n.t("name") },
}
export function RolePage() {
  const { t } = useTranslation()
  const [getPermissions, permissions] = useLazyGetPermissionsQuery()
  const [createRolePermission, createResponse] =
    useCreateRolePermissionsMutation()
  const [getRoles, roles] = useLazyGetRolesQuery()
  const [removeRolePermission, removeResponse] =
    useRemoveRolePermissionsMutation()
  const [selectedRole, setSelectedRole] = useState<Role>()
  const [loadingId, setLoadingId] = useState<string>("")
  const [permissionModalOpen, setPermissionModalOpen] = useState<boolean>(false)
  const [getRolePermissions, rolePermissions] =
    useLazyGetAvailableRolePermissionsQuery()
  const [collection, setCollection] = useState<CollectionQuery>({
    skip: 0,
    top: 15,
  })
  const [collectionRequest, setCollectionRequest] = useState<CollectionQuery>({
    skip: 0,
    top: 15,
  })
  // useEffect(() => {
  //   getRoles({
  //     skip: 0,
  //     orderBy: [{ field: "name", direction: "asc" }],
  //     filter: [[{ field: "protected", value: false, operator: "=" }]],
  //   })
  // }, [getRoles])
  useEffect(() => {
    getPermissions(collection)
    async function getRoleData() {
      const role = await getRoles({
        skip: 0,
        orderBy: [{ field: "name", direction: "asc" }],
        filter: [[{ field: "protected", value: false, operator: "=" }]],
      })
      if (role.data && !selectedRole) {
        setSelectedRole(role.data?.data[0])
      }
    }
    getRoleData()
  }, [collection])
  useEffect(() => {
    if (selectedRole) {
      getRolePermissions({
        roleId: selectedRole?.id,
        collection: collectionRequest,
      })
    }
  }, [selectedRole, collectionRequest])

  const title = (
    <div className="w-full h-8 items-center px-4 text-white flex justify-between gap-2">
      <div>{t("permission", { count: 2 })}</div>
      <div>
        <Button
          leftIcon={<IconArrowBadgeRight />}
          size="xs"
          onClick={() => setPermissionModalOpen(true)}
          className=" border border-white"
        >
          {t("assign")}
        </Button>
      </div>
    </div>
  )
  return (
    <main className="max-w-7xl mx-auto py-6 px-4 lg:px-8 mt-16">
      <Card
        title={t("addPermissionToRole")}
        classNames={{ header: "w-full text-white", body: "" }}
      >
        <div className="w-full flex flex-col md:flex-row gap-4 justify-center space-x-0 md:space-x-8 p-4 ">
          <div className="w-full md:w-2/4 h-full bg-gray-800 bg-opacity-50 backdrop-blur-md overflow-hidden shadow-lg rounded-xl border border-gray-700 px-4">
            <table className="min-w-full divide-y divide-gray-700">
              <thead>
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    <div className="w-full flex justify-between items-center">
                      <div>{t("role", { count: 1 })}</div>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {roles?.data?.data?.map((role) => {
                  return (
                    <tr className="mt-4">
                      <td
                        onClick={() => setSelectedRole(role)}
                        className={`cursor-pointer px-4 py-2 whitespace-nowrap text-sm text-gray-300 rounded-lg hover:bg-gray-500  hover:text-white ${
                          selectedRole?.id === role.id
                            ? "bg-gray-700 text-white"
                            : ""
                        }`}
                      >
                        {role.name}
                      </td>
                    </tr>
                  )
                })}
                {roles?.isLoading && (
                  <tr>
                    <td>
                      <LoadingOverlay visible />
                      <EmptyIcon />
                    </td>
                  </tr>
                )}
                {roles?.data?.data?.length === 0 && (
                  <tr>
                    <td>
                      <EmptyIcon />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className=" w-full bg-gray-800 bg-opacity-50 backdrop-blur-md overflow-scroll shadow-lg rounded-xl border border-gray-700">
            <table className="min-w-full divide-y divide-gray-700">
              <LoadingOverlay visible={rolePermissions?.isFetching} />
              <thead>
                <tr>
                  <th className="px-0 md:px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    {title}
                  </th>
                </tr>
              </thead>
              <tbody>
                {rolePermissions?.data?.data?.map((permission: Permission) => {
                  return (
                    <tr>
                      <td className="flex justify-between p-1 hover:opacity-60 cursor-pointer">
                        <span>{permission.name}</span>
                        <ActionIcon
                          onClick={() => {
                            setLoadingId(permission.id)
                            removeRolePermission({
                              roleId: selectedRole?.id,
                              permissionId: permission?.id,
                            })
                          }}
                          loading={
                            removeResponse?.isLoading &&
                            loadingId === permission.id
                          }
                          color={"red"}
                          variant="default"
                          className="text-danger"
                        >
                          <IconTrash size="1rem" />
                        </ActionIcon>
                      </td>
                    </tr>
                  )
                })}
                {rolePermissions?.isLoading && (
                  <tr>
                    {" "}
                    <td>
                      <LoadingOverlay visible />
                      <EmptyIcon />
                    </td>
                  </tr>
                )}
                {rolePermissions?.data?.data?.length === 0 && (
                  <tr>
                    <td>
                      <EmptyIcon />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            <div className="w-full flex justify-end mt-4 mb-4 px-2">
              <Pagination
                total={rolePermissions?.data?.count ?? 0}
                pageSize={[5, 10, 15, 100]}
                defaultPageSize={15}
                onPaginationChange={(skip: number, top: number) => {
                  setCollectionRequest({ skip: skip, top: top })
                }}
              />
            </div>
          </div>
          <Suspense>
            <ModalCollectionSelector
              config={collectionSelectorModalConfig}
              hasSort={true}
              selectedRows={rolePermissions?.data?.data}
              total={permissions.data?.count}
              items={permissions.data?.data?.filter((permission) => {
                return !rolePermissions?.data?.data?.some((rolePermission) => {
                  return rolePermission.id === permission.id
                })
              })}
              itemsLoading={permissions.isLoading || permissions?.isFetching}
              onPaginationChange={(skip: number, top: number) =>
                setCollection({ ...collection, skip: skip, top: top })
              }
              onDone={(data) => {
                const selectedId = data.map((permission: Permission) => {
                  return permission.id
                })
                createRolePermission({
                  roleId: selectedRole?.id,
                  permissions: selectedId,
                })
              }}
              setModalOpened={(data) => {
                setPermissionModalOpen(data)
              }}
              modalOpened={permissionModalOpen}
              search={debounce((key) => {
                setCollection({
                  ...collection,
                  search: key,
                  searchFrom: ["name", "key"],
                })
              }, 1000)}
            />
          </Suspense>
        </div>
      </Card>
    </main>
  )
}
